import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, Loader2 } from "lucide-react";
import { Pill } from "@/components/layout/PageShell";
import MasterCrudShell from "@/components/layout/MasterCrudShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { useCurrency } from "@/hooks/useCurrency";
import { toast } from "sonner";

const SUBTYPES = ["Walk-in", "Regular", "VIP", "Corporate"];

const EMPTY = {
  code: "",
  name: "",
  type: "Regular",
  status: "Active",
  contact: "",
  phone: "",
  mobile: "",
  email: "",
  fax: "",
  address: "",
  country: "UAE",
  credit_period: 30,
  credit: 0,
  balance: 0,
  opening_date: "2026-01-01",
  account_type: "Debit",
  tax_number: "",
  emirates_id: "",
  emirates_id_issued_on: "",
  emirates_id_expires_on: "",
  nationality: "",
  is_corporate: false,
  billing_cycle: "",
  loyalty_enrolled: true,
};

function customerToForm(c) {
  if (!c) return { ...EMPTY };
  return {
    code: c.code || "",
    name: c.name || "",
    type: c.type || "Regular",
    status: c.status || "Active",
    contact: c.contact || "",
    phone: c.phone || "",
    mobile: c.mobile || c.phone || "",
    email: c.email || "",
    fax: c.fax || "",
    address: c.address || "",
    country: c.country || "UAE",
    credit_period: c.credit_period ?? 30,
    credit: c.credit ?? 0,
    balance: c.balance ?? 0,
    opening_date: c.opening_date || "2026-01-01",
    account_type: c.account_type || "Debit",
    tax_number: c.tax_number || "",
    emirates_id: c.emirates_id || "",
    emirates_id_issued_on: c.emirates_id_issued_on || "",
    emirates_id_expires_on: c.emirates_id_expires_on || "",
    nationality: c.nationality || "",
    is_corporate: Boolean(c.is_corporate),
    billing_cycle: c.billing_cycle || "",
    loyalty_enrolled: c.loyalty_enrolled !== false,
  };
}

function formToPayload(form) {
  return {
    code: form.code,
    name: form.name,
    subtype: form.type,
    status: form.status,
    contact_person: form.contact || null,
    phone: form.phone || null,
    mobile: form.mobile || null,
    email: form.email || null,
    fax: form.fax || null,
    address: form.address || null,
    country: form.country,
    credit_period: Number(form.credit_period) || 0,
    credit_limit: Number(form.credit) || 0,
    opening_balance: Number(form.balance) || 0,
    balance: Number(form.balance) || 0,
    opening_date: form.opening_date || null,
    account_type: form.account_type,
    tax_number: form.tax_number || null,
    emirates_id: form.emirates_id?.trim() || null,
    emirates_id_issued_on: form.emirates_id_issued_on || null,
    emirates_id_expires_on: form.emirates_id_expires_on || null,
    nationality: form.nationality || null,
    is_corporate: Boolean(form.is_corporate),
    billing_cycle: form.billing_cycle || null,
    loyalty_enrolled: Boolean(form.loyalty_enrolled),
  };
}

function Field({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

export default function CustomerMaster() {
  const { user } = useAuth();
  const { format, step: moneyStep, code: currencyCode } = useCurrency();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [view, setView] = useState("form");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers", branchId],
    queryFn: async () => {
      const { data } = await api.get("/customers", { params: { branch_id: branchId } });
      return data.customers;
    },
    enabled: Boolean(branchId),
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.code?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.mobile?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.tax_number?.toLowerCase().includes(q) ||
        c.emirates_id?.toLowerCase().includes(q),
    );
  }, [customers, search]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = formToPayload(form);
      if (editingId) {
        return api.put(`/customers/${editingId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/customers", payload, { params: { branch_id: branchId } });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["customers", branchId] });
      toast.success(res.data.message);
      setEditingId(null);
      setForm(EMPTY);
      setView("form");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to save customer");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/customers/${id}`, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", branchId] });
      toast.success("Customer deleted");
    },
    onError: () => toast.error("Failed to delete customer"),
  });

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function openNew() {
    setEditingId(null);
    const next = customers.length + 1;
    setForm({ ...EMPTY, code: `CUST-${String(next).padStart(3, "0")}` });
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm(customerToForm(row));
    setView("form");
  }

  function handleSave() {
    if (!form.code || !form.name) {
      toast.error("Code and name are required.");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <MasterCrudShell
      title="Customer Master"
      subtitle={`${customers.length} customers — ${user?.default_branch?.name || "branch"}`}
      view={view}
      onViewChange={setView}
      isNew={!editingId}
      onSave={handleSave}
      saving={saveMutation.isPending}
      saveDisabled={!form.code || !form.name}
      onAddNew={openNew}
      testIdPrefix="cust"
      formTitle={editingId ? form.name || "Edit Customer" : "Add New Customer"}
      formSubtitle={editingId ? "Edit" : "Create"}
      list={
        <>
          <DataToolbar
            searchValue={search}
            onSearch={setSearch}
            testIdPrefix="cust"
          />
          {isLoading ? (
            <div className="h-32 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <DataTable
              testIdPrefix="cust"
              emptyText="No customers yet"
              onRowClick={openEdit}
              columns={[
                { key: "code", label: "Code", mono: true, bold: true },
                { key: "name", label: "Name", bold: true },
                { key: "type", label: "Type" },
                { key: "phone", label: "Phone", render: (v, row) => v || row.mobile || "—" },
                { key: "email", label: "Email", render: (v) => v || "—" },
                { key: "tax_number", label: currencyCode === "OMR" ? "VATIN" : "TRN", mono: true, render: (v) => v || "—" },
                { key: "emirates_id", label: "Emirates ID", mono: true, render: (v) => v || "—" },
                {
                  key: "emirates_id_expires_on",
                  label: "EID expiry",
                  render: (v) => v || "—",
                },
                {
                  key: "balance",
                  label: "Balance",
                  align: "right",
                  render: (v) => format(v || 0),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (v) => <Pill tone={v === "Active" ? "green" : v === "Blacklisted" ? "red" : "default"}>{v}</Pill>,
                },
              ]}
              rows={filtered}
              rowActions={(row) => [
                { title: "Edit", icon: <Edit3 size={14} />, onClick: () => openEdit(row) },
                {
                  title: "Delete",
                  icon: <Trash2 size={14} />,
                  danger: true,
                  onClick: () => {
                    if (window.confirm(`Delete customer ${row.name}?`)) {
                      deleteMutation.mutate(row.id);
                    }
                  },
                },
              ]}
            />
          )}
        </>
      }
    >
      <Tabs key={editingId || "new"} defaultValue="basic">
        <TabsList className="bg-slate-100 h-9">
          <TabsTrigger value="basic" className="text-[12.5px] h-7 px-3" data-testid="tab-basic">
            Basic
          </TabsTrigger>
          <TabsTrigger value="contact" className="text-[12.5px] h-7 px-3" data-testid="tab-contact">
            Contact
          </TabsTrigger>
          <TabsTrigger value="finance" className="text-[12.5px] h-7 px-3" data-testid="tab-finance">
            Financial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <Field label="Code *">
              <Input
                value={form.code}
                onChange={(e) => setField("code", e.target.value)}
                className="h-9 font-mono"
                disabled={Boolean(editingId)}
                autoFocus={!editingId}
              />
            </Field>
            <Field label="Name *">
              <Input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                className="h-9"
                autoFocus={Boolean(editingId)}
              />
            </Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setField("type", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUBTYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Blacklisted">Blacklisted</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Emirates ID">
              <Input
                value={form.emirates_id}
                onChange={(e) => setField("emirates_id", e.target.value)}
                className="h-9 font-mono"
                placeholder="784-XXXX-XXXXXXX-X"
              />
            </Field>
            <Field label="Emirates ID start">
              <Input
                type="date"
                value={form.emirates_id_issued_on}
                onChange={(e) => setField("emirates_id_issued_on", e.target.value)}
                className="h-9"
              />
            </Field>
            <Field label="Emirates ID expiry">
              <Input
                type="date"
                value={form.emirates_id_expires_on}
                onChange={(e) => setField("emirates_id_expires_on", e.target.value)}
                className="h-9"
              />
            </Field>
            <Field label="Nationality">
              <Input
                value={form.nationality}
                onChange={(e) => setField("nationality", e.target.value)}
                className="h-9"
                placeholder="UAE, India, etc."
              />
            </Field>
            <Field label="Corporate account">
              <div className="h-9 flex items-center gap-2">
                <Switch checked={form.is_corporate} onCheckedChange={(v) => setField("is_corporate", v)} />
                <span className="text-sm text-slate-600">B2B invoicing / TRN on bills</span>
              </div>
            </Field>
            <Field label="Billing cycle">
              <Select
                value={form.billing_cycle || "none"}
                onValueChange={(v) => setField("billing_cycle", v === "none" ? "" : v)}
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Loyalty enrolled" className="md:col-span-2">
              <div className="h-9 flex items-center gap-2">
                <Switch
                  checked={form.loyalty_enrolled}
                  onCheckedChange={(v) => setField("loyalty_enrolled", v)}
                />
                <span className="text-sm text-slate-600">Earn & redeem points</span>
              </div>
            </Field>
          </div>
        </TabsContent>

        <TabsContent value="contact" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <Field label="Address" className="md:col-span-2">
              <Input value={form.address} onChange={(e) => setField("address", e.target.value)} className="h-9" />
            </Field>
            <Field label="Country">
              <Select value={form.country} onValueChange={(v) => setField("country", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UAE">UAE</SelectItem>
                  <SelectItem value="KSA">Saudi Arabia</SelectItem>
                  <SelectItem value="OMN">Oman</SelectItem>
                  <SelectItem value="QAT">Qatar</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Contact Person">
              <Input value={form.contact} onChange={(e) => setField("contact", e.target.value)} className="h-9" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="h-9" />
            </Field>
            <Field label="Mobile">
              <Input value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} className="h-9" />
            </Field>
            <Field label="Fax">
              <Input value={form.fax} onChange={(e) => setField("fax", e.target.value)} placeholder="—" className="h-9" />
            </Field>
            <Field label="Email">
              <Input value={form.email} onChange={(e) => setField("email", e.target.value)} className="h-9" />
            </Field>
          </div>
        </TabsContent>

        <TabsContent value="finance" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <Field label="Credit Period (days)">
              <Input
                type="number"
                value={form.credit_period}
                onChange={(e) => setField("credit_period", e.target.value)}
                className="h-9 text-right"
              />
            </Field>
            <Field label={`Credit Limit (${currencyCode})`}>
              <Input
                type="number"
                step={moneyStep}
                value={form.credit}
                onChange={(e) => setField("credit", e.target.value)}
                className="h-9 font-mono text-right"
              />
            </Field>
            <p className="md:col-span-2 text-[11px] text-slate-500 -mt-1">
              For credit customers, set Emirates ID and expiry on the Basic tab so POS can check they are still in the country.
            </p>
            <Field label={`Opening Balance (${currencyCode})`}>
              <Input
                type="number"
                step={moneyStep}
                value={form.balance}
                onChange={(e) => setField("balance", e.target.value)}
                className="h-9 font-mono text-right"
              />
            </Field>
            <Field label="Opening Date">
              <Input
                type="date"
                value={form.opening_date}
                onChange={(e) => setField("opening_date", e.target.value)}
                className="h-9"
              />
            </Field>
            <Field label="Account Type">
              <Select value={form.account_type} onValueChange={(v) => setField("account_type", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Debit">Debit</SelectItem>
                  <SelectItem value="Credit">Credit</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={currencyCode === "OMR" ? "VATIN Number" : "VAT/TRN Number"}>
              <Input
                value={form.tax_number}
                onChange={(e) => setField("tax_number", String(e.target.value || "").replace(/\D/g, "").slice(0, 15))}
                placeholder="100xxxxxxxx00003"
                maxLength={15}
                inputMode="numeric"
                className="h-9 font-mono"
              />
            </Field>
          </div>
        </TabsContent>
      </Tabs>
    </MasterCrudShell>
  );
}
